import type { ResumeColorScheme } from '../types';

const TEMPLATE_STYLE_FAMILY_MAP: Record<string, string> = {
  professional: 'classic',
  executive: 'classic',
  creative: 'modern',
  technical: 'modern',
  academic: 'minimal',
  compact: 'minimal',
  elegant: 'minimal',
};

export function resolveTemplateStyleFamily(templateId: string): string {
  return TEMPLATE_STYLE_FAMILY_MAP[templateId] ?? templateId;
}

export function isColorSchemeAvailableForTemplate(scheme: ResumeColorScheme, templateId: string): boolean {
  if (!scheme.templateIds?.length) return true;

  const familyId = resolveTemplateStyleFamily(templateId);
  return scheme.templateIds.includes(templateId) || (familyId !== templateId && scheme.templateIds.includes(familyId));
}

export function getPrioritizedColorSchemeIds(templateId: string): string[] {
  const familyId = resolveTemplateStyleFamily(templateId);

  if (familyId === 'focus-sidebar') {
    return [
      'focus-standard',
      'studio-cobalt',
      'studio-slate-mint',
      'studio-plum-sand',
      'focus-midnight-teal',
      'focus-plum-copper',
      'charcoal',
    ];
  }

  if (familyId === 'modern') {
    return ['studio-cobalt', 'studio-slate-mint', 'studio-plum-sand', 'charcoal'];
  }

  return ['charcoal'];
}

export const RESUME_COLOR_SCHEMES: ResumeColorScheme[] = [
  {
    id: 'focus-standard',
    name: 'Focus Standard',
    templateIds: ['focus-sidebar'],
    colors: {
      primary: '#1b2a52',
      secondary: '#152148',
      accent: '#ff9f70',
      text: '#2a385f',
      background: '#f7f9ff',
      muted: '#6b789f',
    },
    templateOverrides: {
      'focus-sidebar': {
        '--sidebar-bg': '#152148',
        '--sidebar-text': '#f3f6ff',
        '--sidebar-muted': '#b4bfde',
        '--sidebar-divider': 'rgba(255, 255, 255, 0.16)',
        '--sidebar-pill-bg': 'rgba(255, 255, 255, 0.08)',
        '--sidebar-pill-border': 'rgba(255, 255, 255, 0.2)',
        '--content-bg': '#f7f9ff',
        '--content-title': '#1b2a52',
        '--body-text': '#2a385f',
        '--muted-text': '#6b789f',
        '--accent-strong': '#ff9f70',
        '--timeline-line': '#dbe2f3',
        '--entry-dot': '#f18a58',
        '--entry-dot-border': '#f5b08e',
        '--avatar-bg': '#d48b6e',
        '--avatar-border': 'rgba(255, 255, 255, 0.4)',
      },
    },
  },
  {
    id: 'focus-midnight-teal',
    name: 'Focus Midnight Teal',
    templateIds: ['focus-sidebar'],
    colors: {
      primary: '#1d4f73',
      secondary: '#10253d',
      accent: '#21b7a8',
      text: '#24364c',
      background: '#f3f8fb',
      muted: '#6b8299',
    },
    templateOverrides: {
      'focus-sidebar': {
        '--sidebar-bg': '#10253d',
        '--sidebar-text': '#edf6ff',
        '--sidebar-muted': '#a4bdd4',
        '--sidebar-divider': 'rgba(237, 246, 255, 0.18)',
        '--sidebar-pill-bg': 'rgba(237, 246, 255, 0.08)',
        '--sidebar-pill-border': 'rgba(237, 246, 255, 0.24)',
        '--content-bg': '#f3f8fb',
        '--content-title': '#1d4f73',
        '--body-text': '#24364c',
        '--muted-text': '#6b8299',
        '--accent-strong': '#21b7a8',
        '--timeline-line': '#cddce7',
        '--entry-dot': '#21b7a8',
        '--entry-dot-border': '#176e75',
        '--avatar-bg': '#1f8a8c',
        '--avatar-border': 'rgba(237, 246, 255, 0.32)',
      },
    },
  },
  {
    id: 'focus-plum-copper',
    name: 'Focus Plum Copper',
    templateIds: ['focus-sidebar'],
    colors: {
      primary: '#5a3d64',
      secondary: '#2c1f3e',
      accent: '#e4a46e',
      text: '#352b42',
      background: '#f8f5fa',
      muted: '#857191',
    },
    templateOverrides: {
      'focus-sidebar': {
        '--sidebar-bg': '#2c1f3e',
        '--sidebar-text': '#f7f2ff',
        '--sidebar-muted': '#cab7df',
        '--sidebar-divider': 'rgba(247, 242, 255, 0.18)',
        '--sidebar-pill-bg': 'rgba(247, 242, 255, 0.1)',
        '--sidebar-pill-border': 'rgba(247, 242, 255, 0.26)',
        '--content-bg': '#f8f5fa',
        '--content-title': '#5a3d64',
        '--body-text': '#352b42',
        '--muted-text': '#857191',
        '--accent-strong': '#e4a46e',
        '--timeline-line': '#decfe8',
        '--entry-dot': '#e4a46e',
        '--entry-dot-border': '#9b6d4d',
        '--avatar-bg': '#bc7f59',
        '--avatar-border': 'rgba(247, 242, 255, 0.36)',
      },
    },
  },
  {
    id: 'studio-cobalt',
    name: 'Studio Cobalt',
    templateIds: ['focus-sidebar', 'modern'],
    colors: {
      primary: '#2953a8',
      secondary: '#162b4d',
      accent: '#f0a36b',
      text: '#253753',
      background: '#f5f8ff',
      muted: '#6e7f9d',
    },
    templateOverrides: {
      'focus-sidebar': {
        '--sidebar-bg': '#162b4d',
        '--sidebar-text': '#f3f7ff',
        '--sidebar-muted': '#b6c6de',
        '--sidebar-divider': 'rgba(243, 247, 255, 0.18)',
        '--sidebar-pill-bg': 'rgba(243, 247, 255, 0.08)',
        '--sidebar-pill-border': 'rgba(243, 247, 255, 0.24)',
        '--content-bg': '#f5f8ff',
        '--content-title': '#2953a8',
        '--body-text': '#253753',
        '--muted-text': '#6e7f9d',
        '--accent-strong': '#f0a36b',
        '--timeline-line': '#d6e0f0',
        '--entry-dot': '#f0a36b',
        '--entry-dot-border': '#cb7d47',
        '--avatar-bg': '#2e4671',
        '--avatar-border': 'rgba(243, 247, 255, 0.32)',
      },
      modern: {
        '--modern-h1': '#162b4d',
        '--modern-heading': '#2953a8',
        '--modern-rule': '#f0a36b',
        '--modern-link': '#162b4d',
        '--modern-skill-text': '#162b4d',
        '--modern-skill-border': '#d68b57',
      },
    },
  },
  {
    id: 'studio-slate-mint',
    name: 'Studio Slate Mint',
    templateIds: ['focus-sidebar', 'modern'],
    colors: {
      primary: '#1f5f69',
      secondary: '#1c2f3c',
      accent: '#48b38e',
      text: '#273a46',
      background: '#f4f8f8',
      muted: '#6f838e',
    },
    templateOverrides: {
      'focus-sidebar': {
        '--sidebar-bg': '#1c2f3c',
        '--sidebar-text': '#eef7fa',
        '--sidebar-muted': '#b4cad2',
        '--sidebar-divider': 'rgba(238, 247, 250, 0.18)',
        '--sidebar-pill-bg': 'rgba(238, 247, 250, 0.08)',
        '--sidebar-pill-border': 'rgba(238, 247, 250, 0.24)',
        '--content-bg': '#f4f8f8',
        '--content-title': '#1f5f69',
        '--body-text': '#273a46',
        '--muted-text': '#6f838e',
        '--accent-strong': '#48b38e',
        '--timeline-line': '#d5e1e3',
        '--entry-dot': '#48b38e',
        '--entry-dot-border': '#2f7f67',
        '--avatar-bg': '#2f4f5e',
        '--avatar-border': 'rgba(238, 247, 250, 0.32)',
      },
      modern: {
        '--modern-h1': '#1c2f3c',
        '--modern-heading': '#1f5f69',
        '--modern-rule': '#48b38e',
        '--modern-link': '#1c2f3c',
        '--modern-skill-text': '#1c2f3c',
        '--modern-skill-border': '#359775',
      },
    },
  },
  {
    id: 'studio-plum-sand',
    name: 'Studio Plum Sand',
    templateIds: ['focus-sidebar', 'modern'],
    colors: {
      primary: '#724968',
      secondary: '#31253f',
      accent: '#d69b74',
      text: '#3d3249',
      background: '#f8f5f7',
      muted: '#8a788f',
    },
    templateOverrides: {
      'focus-sidebar': {
        '--sidebar-bg': '#31253f',
        '--sidebar-text': '#f8f2ff',
        '--sidebar-muted': '#cebddd',
        '--sidebar-divider': 'rgba(248, 242, 255, 0.2)',
        '--sidebar-pill-bg': 'rgba(248, 242, 255, 0.1)',
        '--sidebar-pill-border': 'rgba(248, 242, 255, 0.28)',
        '--content-bg': '#f8f5f7',
        '--content-title': '#724968',
        '--body-text': '#3d3249',
        '--muted-text': '#8a788f',
        '--accent-strong': '#d69b74',
        '--timeline-line': '#dfd3e5',
        '--entry-dot': '#d69b74',
        '--entry-dot-border': '#a57456',
        '--avatar-bg': '#513a66',
        '--avatar-border': 'rgba(248, 242, 255, 0.34)',
      },
      modern: {
        '--modern-h1': '#31253f',
        '--modern-heading': '#724968',
        '--modern-rule': '#d69b74',
        '--modern-link': '#31253f',
        '--modern-skill-text': '#31253f',
        '--modern-skill-border': '#b48362',
      },
    },
  },
  {
    id: 'default',
    name: 'Professional Blue',
    colors: {
      primary: '#2563eb',
      secondary: '#1e40af',
      accent: '#3b82f6',
      text: '#1f2937',
      background: '#ffffff',
      muted: '#6b7280',
    },
  },
  {
    id: 'charcoal',
    name: 'Charcoal',
    colors: {
      primary: '#374151',
      secondary: '#1f2937',
      accent: '#4b5563',
      text: '#111827',
      background: '#ffffff',
      muted: '#6b7280',
    },
  },
  {
    id: 'graphite-gold',
    name: 'Graphite Gold',
    colors: {
      primary: '#3f4e63',
      secondary: '#1f2937',
      accent: '#b58a45',
      text: '#111827',
      background: '#ffffff',
      muted: '#64748b',
    },
  },
  {
    id: 'ocean-ink',
    name: 'Ocean Ink',
    colors: {
      primary: '#24518f',
      secondary: '#152a44',
      accent: '#2c9a91',
      text: '#24364d',
      background: '#ffffff',
      muted: '#677993',
    },
  },
  {
    id: 'forest',
    name: 'Forest Green',
    colors: {
      primary: '#166534',
      secondary: '#14532d',
      accent: '#22c55e',
      text: '#1f2937',
      background: '#ffffff',
      muted: '#6b7280',
    },
  },
  {
    id: 'burgundy',
    name: 'Burgundy',
    colors: {
      primary: '#7f1d1d',
      secondary: '#991b1b',
      accent: '#dc2626',
      text: '#1f2937',
      background: '#ffffff',
      muted: '#6b7280',
    },
  },
  {
    id: 'navy',
    name: 'Navy',
    colors: {
      primary: '#1e3a5f',
      secondary: '#0c4a6e',
      accent: '#0ea5e9',
      text: '#1f2937',
      background: '#ffffff',
      muted: '#6b7280',
    },
  },
  {
    id: 'purple',
    name: 'Royal Purple',
    colors: {
      primary: '#6b21a8',
      secondary: '#581c87',
      accent: '#a855f7',
      text: '#1f2937',
      background: '#ffffff',
      muted: '#6b7280',
    },
  },
  {
    id: 'teal',
    name: 'Teal',
    colors: {
      primary: '#0d9488',
      secondary: '#115e59',
      accent: '#14b8a6',
      text: '#1f2937',
      background: '#ffffff',
      muted: '#6b7280',
    },
  },
  {
    id: 'slate',
    name: 'Slate',
    colors: {
      primary: '#475569',
      secondary: '#334155',
      accent: '#64748b',
      text: '#1e293b',
      background: '#ffffff',
      muted: '#94a3b8',
    },
  },
  {
    id: 'amber',
    name: 'Amber Gold',
    colors: {
      primary: '#b45309',
      secondary: '#92400e',
      accent: '#f59e0b',
      text: '#1f2937',
      background: '#ffffff',
      muted: '#6b7280',
    },
  },
  {
    id: 'rose',
    name: 'Rose',
    colors: {
      primary: '#be185d',
      secondary: '#9d174d',
      accent: '#ec4899',
      text: '#1f2937',
      background: '#ffffff',
      muted: '#6b7280',
    },
  },
];

export function getColorScheme(id: string): ResumeColorScheme | undefined {
  return RESUME_COLOR_SCHEMES.find((cs) => cs.id === id);
}

export function getDefaultColorScheme(): ResumeColorScheme {
  return RESUME_COLOR_SCHEMES.find((scheme) => scheme.id === 'charcoal') ?? RESUME_COLOR_SCHEMES[0];
}

/**
 * Generate CSS variables for a color scheme
 */
export function generateColorSchemeCSS(scheme: ResumeColorScheme): string {
  return `
    --resume-color-primary: ${scheme.colors.primary};
    --resume-color-secondary: ${scheme.colors.secondary};
    --resume-color-accent: ${scheme.colors.accent};
    --resume-color-text: ${scheme.colors.text};
    --resume-color-background: ${scheme.colors.background};
    --resume-color-muted: ${scheme.colors.muted};
  `;
}

export function generateTemplateOverrideCSS(scheme: ResumeColorScheme, templateId: string): string {
  const familyId = resolveTemplateStyleFamily(templateId);
  const overrides = scheme.templateOverrides?.[templateId] ?? scheme.templateOverrides?.[familyId];
  if (!overrides) return '';

  const entries = Object.entries(overrides)
    .map(([key, value]) => `    ${key}: ${value};`)
    .join('\n');

  return entries ? `\n${entries}\n` : '';
}
